import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sidebar } from "../components/sidebar";
import { ChatHeader } from "../components/chat-header";
import { ChatInput } from "../components/chat-input";
import { ChatMessages } from "../components/chat-messages";
import { AddVehicleDialog } from "../components/add-vehicle-dialog";
import { RateLimitModal } from "../components/rate-limit-modal";
import { SourcesPanel } from "../components/sources-panel";
import { useMobile } from "../hooks/use-mobile";
import { chatService } from "../services/chatService";
import { apiService } from "../services/apiService";
import { Chat, Message }from "../types/chat";
import { useVehicle } from "../contexts/VehicleContext";
import { useAuth } from "../contexts/AuthContext";
import { vehicleService } from "../services/vehicleService";
import { useParams, useNavigate } from 'react-router-dom';
import { SourcesManager, Source } from "../lib/sources-utils";

const ChatPage = () => {
  const [chats, setChats] = useState<Chat[]>([]);
  // Track when initial chats have been loaded to avoid premature auto-creation
  const [chatsLoaded, setChatsLoaded] = useState(false);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [ isLoading, setIsLoading] = useState(false);
  const [streamedMessage, setStreamedMessage] = useState(""); // Add state for streamed message
  const [vehicleName, setVehicleName] = useState("");
  const [isAutoCreatingChat, setIsAutoCreatingChat] = useState(false); // Prevent multiple auto-creations
  const [showSources, setShowSources] = useState(false);
  const [highlightedSourceId, setHighlightedSourceId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { selectedVehicle, vehicles, vehiclesLoading } = useVehicle();
  const { getFreshToken } = useAuth(); 
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();

  const isMobile = useMobile();

  // Initialize sources manager
  const sourcesManagerRef = useRef(new SourcesManager());
  const [currentSources, setCurrentSources] = useState<Source[]>([]);

  useEffect(() => {
    if (isMobile) {
      setShowSidebar(false);
      setShowSources(false);
    }
  }, [isMobile]);

  // Auto-open Add Vehicle dialog when user has no vehicles (after loading completes)
  useEffect(() => {
    if (!vehiclesLoading && vehicles.length === 0 && !showAddVehicle) {
      setShowAddVehicle(true);
    }
  }, [vehiclesLoading, vehicles.length, showAddVehicle]);

  // Auto-create a chat when a vehicle is added and there are no existing chats
  useEffect(() => {
    const autoCreateChatForNewVehicle = async () => {
      // Only proceed if:
      // 1. We have a selected vehicle
      // 2. There are no existing chats after chats have definitively loaded
      // 3. Vehicles are done loading
      // 4. We're not already in the process of auto-creating a chat
      // 5. There is no chatId in the URL (user isn't explicitly loading an existing chat)
      if (
        selectedVehicle &&
        chatsLoaded &&
        chats.length === 0 &&
        !vehiclesLoading &&
        !isAutoCreatingChat &&
        !chatId
      ) {
        setIsAutoCreatingChat(true);
        try {
          const freshToken = await getFreshToken();
          const newChat: Chat = await chatService.createNewChat(selectedVehicle, freshToken);
          setChats([newChat]);
          setCurrentChatId(newChat.id);
          navigate(`/chat/${newChat.id}`, { replace: true });
          console.log('Auto-created chat for new vehicle:', selectedVehicle.make, selectedVehicle.model);
        } catch (error) {
          console.error("Error auto-creating chat for new vehicle:", error);
          // Don't show rate limit modal here since this is automatic
        } finally {
          setIsAutoCreatingChat(false);
        }
      }
    };

    autoCreateChatForNewVehicle();
  }, [selectedVehicle, chats.length, vehiclesLoading, chatsLoaded, chatId, navigate, getFreshToken, isAutoCreatingChat]);

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const fetchedChats = await chatService.getAllChats();
        setChats(fetchedChats);
        if (fetchedChats.length > 0) {
          if (chatId && chatId !== "" && fetchedChats.find((chat) => chat.id === chatId)) {
            setCurrentChatId(chatId);
          } else if (fetchedChats[0]?.id) {
            const firstChatId = fetchedChats[0].id;
            setCurrentChatId(firstChatId);
            navigate(`/chat/${firstChatId}`, { replace: true });
          }
        }
      } catch (error) {
        console.error("Error fetching chats:", error);
      } finally {
        setChatsLoaded(true);
      }
    };

    fetchChats();
  }, [chatId, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Auto-create a chat if none exists and user has a vehicle
    if (!currentChatId && selectedVehicle) {
      try {
        const freshToken = await getFreshToken();
        const newChat: Chat = await chatService.createNewChat(selectedVehicle, freshToken);
        setChats(prevChats => prevChats.length === 0 ? [newChat] : [newChat, ...prevChats]);
        setCurrentChatId(newChat.id);
        navigate(`/chat/${newChat.id}`, { replace: true });
        // Continue with message submission using the new chat
      } catch (error) {
        console.error("Error creating new chat:", error);
        if (apiService.isRateLimitError(error)) {
          setShowRateLimitModal(true);
        }
        return; // Don't proceed with message if chat creation failed
      }
    }

    // If still no currentChatId after auto-creation attempt, return
    if (!currentChatId) return;

    setIsLoading(true);
    setStreamedMessage(""); // Clear any previous streamed message

    try {
      const newMessage: Message = {
        role: "user",
        content: input,
        timestamp: Date.now(),
      };

      // Add user message to chat immediately and trigger scroll
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.id === currentChatId
            ? { ...chat, messages: [...chat.messages, newMessage] }
            : chat
        )
      );

      setInput("");

      // Scroll to the new user message immediately
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'end' 
        });
      }, 50);

      const freshToken = await getFreshToken();
      let accumulatedContent = "";
      
      const updatedChat = await chatService.addMessage(
        currentChatId,
        newMessage,
        (chunk) => {
          accumulatedContent += chunk;
          
          // Parse sources from accumulated content
          const parsed = sourcesManagerRef.current.parseAndReplaceLinks(accumulatedContent);
          
          // Update sources if new ones were found
          if (parsed.sources.length > 0) {
            setCurrentSources(sourcesManagerRef.current.getAllSources());
          }
          
          // Set the streamed message with parsed content
          setStreamedMessage(parsed.text);
          setIsLoading(false); // Hide the loading spinner as soon as we start receiving chunks
        },
        freshToken,
        (title) => {
          // Update the chat title immediately in the UI
          setChats((prevChats) =>
            prevChats.map((chat) =>
              chat.id === currentChatId ? { ...chat, title } : chat
            )
          );
        }
      );

      // Handle session rebuilding - if chat ID changed, update the URL and state
      if (updatedChat.id !== currentChatId) {
        console.log(`Chat session rebuilt: ${currentChatId} -> ${updatedChat.id}`);
        setCurrentChatId(updatedChat.id);
        navigate(`/chat/${updatedChat.id}`, { replace: true });
        
        // Update the chats array to replace the old chat with the new one
        setChats((prevChats) => 
          prevChats.map((chat) => 
            chat.id === currentChatId ? updatedChat : chat
          )
        );
      } else {
        // Normal case - just update the existing chat
        setChats((prevChats) =>
          prevChats.map((chat) =>
            chat.id === currentChatId
              ? updatedChat
              : chat
          )
        );
      }

      // Clear streamedMessage only after updating the messages array
      setStreamedMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      
      // Check for session rebuilding failure
      if (error instanceof Error && error.message.includes('Chat session expired and could not be restored')) {
        alert('This chat session has expired and cannot be restored. Please start a new conversation.');
      }
      // Use the robust error checking method from apiService
      else if (apiService.isRateLimitError(error)) {
        setShowRateLimitModal(true);
      } else {
        // Handle other errors - could show a different modal or toast
        console.error("Unexpected error sending message:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectChat = useCallback((selectedChatId: string) => {
    setCurrentChatId(selectedChatId);
    navigate(`/chat/${selectedChatId}`);
    
    // Clear sources when switching chats
    sourcesManagerRef.current.clearSources();
    setCurrentSources([]);
    setStreamedMessage("");
  }, [navigate]);

  const handleCreateNewChat = async () => {
    if (!selectedVehicle) { // TODO handle this better
      alert("No vehicle selected");
      return;
    }

    // Clear the input, streamed message, and sources
    setStreamedMessage("");
    setInput("");
    sourcesManagerRef.current.clearSources();
    setCurrentSources([]);

    try {
      const freshToken = await getFreshToken();
      const newChat: Chat = await chatService.createNewChat(selectedVehicle, freshToken);
      setChats((prevChats) => [newChat, ...prevChats]);
      setCurrentChatId(newChat.id);
      navigate(`/chat/${newChat.id}`); // Navigate to the new chat's URL
    } catch (error) {
      console.error("Error creating new chat:", error);
      
      // Use the robust error checking method from apiService
      if (apiService.isRateLimitError(error)) {
        setShowRateLimitModal(true);
      } else {
        // Handle other errors - could show a different modal or toast
        console.error("Unexpected error creating chat:", error);
      }
    }
  };

  // Fetch vehicle name when current chat changes
  useEffect(() => {
    const fetchVehicleName = async () => {
      const currentChat = chats.find((chat) => chat.id === currentChatId);
      if (currentChat?.vehicleId) {
        try {
          const freshToken = await getFreshToken();
          const name = await vehicleService.getVehicleNameById(currentChat.vehicleId, freshToken || undefined);
          setVehicleName(name);
        } catch (error) {
          console.error("Error fetching vehicle name:", error);
          setVehicleName("");
        }
      } else {
        setVehicleName("");
      }
    };

    fetchVehicleName();
  }, [currentChatId, chats, getFreshToken]);

  // Parse sources from existing messages when chat changes
  useEffect(() => {
    if (currentChatId) {
      const currentChat = chats.find((chat) => chat.id === currentChatId);
      if (currentChat?.messages) {
        // Clear sources and reparse from all messages
        sourcesManagerRef.current.clearSources();
        
        currentChat.messages.forEach((message) => {
          if (message.role === "assistant") {
            sourcesManagerRef.current.parseAndReplaceLinks(message.content);
          }
        });
        
        setCurrentSources(sourcesManagerRef.current.getAllSources());
      }
    }
  }, [currentChatId, chats]);

  return (
    <div className="flex bg-gradient-to-br from-zinc-900 to-black text-white overflow-hidden mobile-viewport safe-area-bottom">
      <Sidebar
        chats={chats.map((chat) => ({ 
          ...chat, 
          name: chat.title,
          vehicleId: chat.vehicleId,
          public: chat.public,
          createdAt: chat.createdAt,
          updatedAt: chat.updatedAt
        }))}
        currentChatId={currentChatId || ""}
        onSelectChat={handleSelectChat}
        onCreateNewChat={handleCreateNewChat}
        onDeleteChat={async (chatIdToDelete) => {
          try {
            await chatService.deleteChat(chatIdToDelete);
            const remainingChats = chats.filter((chat) => chat.id !== chatIdToDelete);
            setChats(remainingChats);
            if (currentChatId === chatIdToDelete) {
              if (remainingChats.length > 0) {
                const newCurrentChatId = remainingChats[0].id;
                setCurrentChatId(newCurrentChatId);
                navigate(`/chat/${newCurrentChatId}`); // Navigate to the next chat
              } else {
                setCurrentChatId(null);
                navigate('/'); // Navigate to home or a default route if no chats left
              }
            }
          } catch (error) {
            console.error("Error deleting chat:", error);
          }
        }}
        onRenameChat={async (chatIdToRename, newName) => {
          try {
            await chatService.renameChat(chatIdToRename, newName);
            setChats((prevChats) =>
              prevChats.map((chat) => (chat.id === chatIdToRename ? { ...chat, title: newName } : chat))
            );
          } catch (error) {
            console.error("Error renaming chat:", error);
          }
        }}
        onShareChat={async (chatIdToShare) => {
          try {
            const chat = chats.find(c => c.id === chatIdToShare);
            if (!chat) return;
            if (!chat.public) {
              const updated: Chat = { ...chat, public: true };
              await chatService.saveChat(updated);
              setChats((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            }
          } catch (error) {
            console.error("Error sharing chat:", error);
          }
        }}
        onCopyPublicUrl={async (chatIdToCopy) => {
          try {
            const publicUrl = `${window.location.origin}/view/${chatIdToCopy}`;
            await navigator.clipboard.writeText(publicUrl);
            // You could add a toast notification here
          } catch (error) {
            console.error("Error copying URL:", error);
          }
        }}
        onStopSharing={async (chatIdToUnshare) => {
          try {
            const chat = chats.find(c => c.id === chatIdToUnshare);
            if (!chat) return;
            if (chat.public) {
              const updated: Chat = { ...chat, public: false };
              await chatService.saveChat(updated);
              setChats((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
            }
          } catch (error) {
            console.error("Error stopping sharing:", error);
          }
        }}
        showSidebar={showSidebar}
        onToggleSidebar={() => setShowSidebar(!showSidebar)}
        onAddVehicle={() => setShowAddVehicle(true)}
      />

      <main className="flex-1 flex flex-col h-full overflow-hidden">
        <ChatHeader
          chatName={chats.find((chat) => chat.id === currentChatId)?.title || "New Chat"}
          vehicle={vehicleName}
          onToggleSidebar={() => setShowSidebar(!showSidebar)}
          onToggleSources={() => setShowSources(!showSources)}
          sourcesOpen={showSources}
          sourcesCount={currentSources.length}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6">
            <ChatMessages
              messages={chats.find((chat) => chat.id === currentChatId)?.messages || []}
              streamedMessage={streamedMessage}
              messagesEndRef={messagesEndRef}
              isLoading={isLoading}
              chatId={currentChatId || undefined}
              sources={currentSources}
              onSourceClick={(source) => {
                setHighlightedSourceId(source.id);
                setShowSources(true);
                // Remove highlight after a few seconds
                setTimeout(() => setHighlightedSourceId(undefined), 3000);
              }}
            />
          </div>
        </div>

        <ChatInput
          input={input}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </main>

      {/* Sources Panel */}
      <SourcesPanel
        sources={currentSources}
        isOpen={showSources}
        onToggle={() => setShowSources(!showSources)}
        onClose={() => setShowSources(false)}
        highlightedSourceId={highlightedSourceId}
        onSourceClick={(source) => {
          // Open URL and highlight the source
          window.open(source.url, '_blank', 'noopener,noreferrer');
          setHighlightedSourceId(source.id);
          // Remove highlight after a few seconds
          setTimeout(() => setHighlightedSourceId(undefined), 3000);
        }}
      />

      <AddVehicleDialog open={showAddVehicle} onOpenChange={setShowAddVehicle} />
      <RateLimitModal open={showRateLimitModal} onOpenChange={setShowRateLimitModal} />
    </div>
  );
};

export default ChatPage;